// src/data/bnYardmasterClass.js
//
// Record 614 (ruling 295): THE PROTECTED / ROA CLASS OF A BN RULE, shown wherever a BN rule is shown.
//
// Source: data\YCP_BN_PROTECTED_ROA_FINDINGS_2026-10-08.md. Burkart (GO-341), msg 1a02c27ab7f68412, 2026-08-23:
// "All but 15 of my Yardmasters are Right of Appointment." (data\YCP_THREAD_BURKART_GO341_SIGNOFF.md:105, as extracted
// there). A BN rule shown with no class leaves a member to guess which side of the line he is on. The agreement's own
// Definitions draw the line (data\agreements\BN_YM_Schedule_1997-05-10.source.txt:152-157): Protected = seniority
// established before November 1, 1994; ROA = on or after (summary, not quotation).
//
// TWO CATALOGUES CARRY THE CLASS, and each in one place only (section XI):
//   * the 75 violation-map rows of the 1994 Memorandum (agreement_key BN-YM-1994) - data\YCP_BN_YARDMASTER_CLASS.csv,
//     joined onto each row by scripts\gen-violation-brief.js and carried out by the Cloud Function on each rule;
//   * the 31 Rules of the 1997 Schedule (src\data\bnCatalog.js, the rulebook picker) - BN_1997_RULE_CLASS below,
//     from section (c-1) of the findings. Five Rules (5, 7, 8, 9, 16) do not take one class; they carry it per paragraph.
//
// yardmasterClass: 'protected' | 'roa' | 'both'
// classBasis:      'text'    - the rule's own words name the class (or the date line) at classSource
//                  'derived' - a reading from verified text (Rule 7E: an ROA man has no bid or displacement right
//                              at all), to be confirmed by Burkart as GO-341's General Chairperson (findings U3)
//                  'silence' - the text names neither class; classSource is the rule's own cited line
// classSource:     file:line of the line the class rests on.
//
// INFORMATION, NEVER A BLOCK. Nothing here gates selecting, saving or filing a rule. A line that cannot be resolved
// is simply not shown. scripts\test-bn-class.mjs proves the counts, every source line, and the wiring.

import { agreementFamily } from './agreementRegistry';   // ruling 312 phase 1 (d)
import CRAFT_WORDS from './craftWords.json';   // section 747: the installation's craft words
export const BN_CLASS_VALUES = ['protected', 'roa', 'both'];
export const BN_CLASS_BASES = ['text', 'derived', 'silence'];

const S97 = 'data/agreements/BN_YM_Schedule_1997-05-10.source.txt';

const PHRASE = {
  protected: 'Protected Yardmasters (seniority before Nov 1, 1994)',
  roa: 'ROA Yardmasters',
  both: 'All yardmasters',
};
const DERIVED_NOTE = ' (reading - to be confirmed by GO-341)';

// The class phrase alone ("Protected Yardmasters (...)"), with the derived note where the basis is a reading.
export function bnClassPhrase(cls, basis) {
  const p = PHRASE[cls];
  if (!p) return null;
  return p + (basis === 'derived' ? DERIVED_NOTE : '');
}

// The whole line as the screen shows it.
export function bnClassText(cls, basis) {
  const p = bnClassPhrase(cls, basis);
  return p ? 'Applies to: ' + p : null;
}

export function isBnAgreement(agreement) {
  return agreementFamily(agreement) === 'bn-ym';   // ruling 312 phase 1 (d): the registry names the agreement
}

const one = (cls, basis, line) => ({ yardmasterClass: cls, classBasis: basis, classSource: S97 + ':' + line });
const part = (paras, cls, basis, line, note) => Object.assign({ paras }, one(cls, basis, line), note ? { note } : {});

// The 31 Rules of the 1997 Schedule, from findings section (c-1). A 'silence' entry cites the Rule's heading line
// (bnCatalog.js sourceLine). Line numbers are those of the source file on disk, read 2026-10-08.
export const BN_1997_RULE_CLASS = {
  '1': one('both', 'silence', 180),
  '2': one('both', 'silence', 218),
  '3': one('both', 'silence', 263),
  '4': one('both', 'text', 325),     // GREB positions are "Right of Appointment" positions; either class may hold one
  '5': { parts: [
    part('5A (first five sentences)', 'roa', 'derived', 157),
    part('5A (last sentence)', 'protected', 'text', 430),
    part('5B', 'roa', 'derived', 157),
  ] },
  '6': one('both', 'silence', 438),
  '7': { parts: [
    part('7A, 7B, 7D, 7F', 'both', 'text', 498),   // keyed to ROA POSITIONS, which either class may hold
    part('7C', 'both', 'text', 503),
    part('7E', 'roa', 'text', 524),
  ] },
  '8': { parts: [
    part('8A, 8C, 8E, 8F, 8G', 'protected', 'text', 533),
    part('8B', 'both', 'silence', 549),
    part('8D, 8H, 8I, 8J', 'both', 'silence', 561),
    part('8K', 'protected', 'text', 619,
      'on non-holidays; PLB 7303 Award 13 applies 8K to all yardmasters on holidays (carried from the record, not re-verified)'),
  ] },
  '9': { parts: [
    part('9A(1)', 'protected', 'text', 650),
    part('9A(2)', 'both', 'text', 655),
    part('9A(2) NOTE', 'protected', 'derived', 659),
    part('9A(3), 9A(4), 9B', 'protected', 'text', 667),
  ] },
  '10': one('both', 'silence', 688),
  '11': one('both', 'silence', 701),
  '12': one('both', 'silence', 713),
  '13': one('both', 'silence', 740),
  '14': one('both', 'silence', 747),
  '15': one('both', 'silence', 755),
  '16': { parts: [
    part('16A-C, 16E', 'both', 'silence', 763),
    part('16D', 'protected', 'text', 783),
  ] },
  '17': one('both', 'silence', 795),
  '18': one('both', 'silence', 804),
  '19': one('both', 'silence', 812),
  '20': one('both', 'silence', 818),
  '21': one('both', 'silence', 825),
  '22': one('both', 'silence', 838),
  '23': one('both', 'silence', 1038),
  '24': one('both', 'silence', 1047),
  '25': one('both', 'silence', 1136),
  '26': one('both', 'silence', 1142),
  '27': one('both', 'silence', 1148),
  '28': one('both', 'silence', 1381),
  '29': one('both', 'silence', 1565),
  '30': one('both', 'silence', 1579),
  '31': one('both', 'silence', 1617),
};

// A 1997 Schedule rule by its picker label ("Rule 8 — Exercise of Seniority"). Only a label that BEGINS "Rule N" is
// read: a 1994 Memorandum label ("SIDE LETTER No. 7", "ARTICLE VI ...") is a different instrument and must never be
// given a 1997 Rule's class by its digits.
export function bnRuleClassLine(label) {
  const m = String(label || '').match(/^\s*Rule\s+(\d+)\b/i);
  const e = m && BN_1997_RULE_CLASS[m[1]];
  if (!e) return null;
  if (!e.parts) return bnClassText(e.yardmasterClass, e.classBasis);
  const bits = e.parts.map(p => {
    const ph = bnClassPhrase(p.yardmasterClass, p.classBasis);
    return ph ? p.paras + ': ' + ph + (p.note ? ' ' + p.note : '') : null;
  }).filter(Boolean);
  return bits.length ? 'Applies to: varies by paragraph - ' + bits.join('; ') : null;
}

// The line for one rule object on a claim or in the picker. A rule carrying its own class (a violation-map row from
// the Cloud Function) shows that; otherwise a BN 1997 Rule label shows the Rule's class; anything else shows nothing.
// Never throws.
export function bnClassLineFor(agreement, rule) {
  try {
    if (!CRAFT_WORDS.showYardmasterClasses) return null;   // section 747: hidden for a T&E installation
    const r = rule || {};
    if (r.yardmasterClass) return bnClassText(r.yardmasterClass, r.classBasis);
    if (!isBnAgreement(agreement)) return null;
    return bnRuleClassLine(r.rule);
  } catch (e) {
    return null;
  }
}
